import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TextField,
  Box,
  Button,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Slide from "@mui/material/Slide";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MuiMultiSelectBottomSheet = ({
  label,
  options,
  onChange,
  value = [],
  error = false,
  helperText = "",
  required = false,
  bottomSheetHeight = "50vh"
}) => {
  const [open, setOpen] = useState(false);
  const [localSelections, setLocalSelections] = useState(value);
  const [hasOpened, setHasOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dialogContentRef = useRef(null);

  useEffect(() => {
    setLocalSelections(value);
  }, [value]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (dialogContentRef.current) {
          dialogContentRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    // Auto-scroll to first match
    if (dialogContentRef.current) {
      const firstItem = dialogContentRef.current.querySelector("li");
      if (firstItem) {
        firstItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [searchTerm]);

  const handleOpen = () => {
    setOpen(true);
    setHasOpened(true);
    setSearchTerm("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  const handleOk = () => {
    if (onChange) {
      onChange({ target: { value: localSelections } });
    }
    setOpen(false);
  };

  const handleToggle = (item) => {
    const updated = localSelections.includes(item)
      ? localSelections.filter((i) => i !== item)
      : [...localSelections, item];
    setLocalSelections(updated);
  };

  const handleKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      event.preventDefault();
    }

    if (event.key === "Backspace") {
      setSearchTerm((prev) => prev.slice(0, -1));
    } else if (event.key === "Escape") {
      if (searchTerm) {
        setSearchTerm("");
      } else {
        handleClose();
      }
    } else if (event.key.length === 1 && event.key.match(/[a-z0-9 ]/i)) {
      setSearchTerm((prev) => prev + event.key);
    }
  };

  const selectedLabel = localSelections.length > 0 ? localSelections.join(", ") : "";

  const displayErrorMessage =
    required && !open && hasOpened && localSelections.length === 0;

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <TextField
        label={label}
        value={selectedLabel}
        placeholder="Velg alternativer"
        onClick={handleOpen}
        fullWidth
        error={displayErrorMessage}
        helperText={displayErrorMessage ? helperText || "Dette feltet er påkrevd" : " "}
        InputLabelProps={{
          shrink: !!selectedLabel,
        }}
        InputProps={{
          readOnly: true,
          endAdornment: <ArrowDropDownIcon />,
          style: {
            cursor: "pointer",
            fontStyle: selectedLabel ? "normal" : "italic",
            color: selectedLabel ? "#000" : "#6F7979",
          },
        }}
        sx={{
          '& .MuiInputLabel-root': {
            color: '#3F4949',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#00696D',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 50px)',
          },
          '& .MuiInputBase-root': {
            padding: '0px',
          },
          '& .MuiInput-input': {
            padding: '16.5px 14px',
          },
        }}
        FormHelperTextProps={{
          sx: { height: '24px', margin: '0px' }
        }}
      />

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.15)",
            backgroundColor: "#F2F4F4",
            height: bottomSheetHeight,
          },
        }}
        BackdropProps={{ onClick: handleClose }}
      >
        <Box
          sx={{
            textAlign: "center",
            padding: "15px 8px",
            cursor: "grab",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 4,
              backgroundColor: "#6F7979",
              borderRadius: 2,
              margin: "0 auto",
            }}
          />
        </Box>

        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            padding: 0,
            "&:focus": {
              outline: "none",
            },
          }}
        >
          <List>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <ListItem
                  key={option}
                  button
                  onClick={() => handleToggle(option)}
                  sx={{
                    height: 50,
                    px: 2,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <ListItemIcon sx={{ padding: "0px 0px 0px 16px" }}>
                    <Checkbox
                      checked={localSelections.includes(option)}
                      edge="start"
                      sx={{
                        color: "#6F7979",
                        "&.Mui-checked": {
                          color: "#00696D",
                          "& .MuiSvgIcon-root": {
                            color: "#00696D",
                          },
                        },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={option} />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No options available"
                  sx={{ textAlign: "center", fontStyle: "italic", color: "#6F7979" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <Button
          variant="contained"
          onClick={handleOk}
          disabled={localSelections.length === 0}
          sx={{
            display: "inline-flex",
            height: "40px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            textTransform: "none",
            backgroundColor: "#00696D",
            color: "#fff",
            borderRadius: "100px",
            margin: "16px auto",
            px: 3,
            "&:hover": {
              backgroundColor: "#005B60",
            },
          }}
        >
          Ok
        </Button>
      </Dialog>
    </>
  );
};

export default MuiMultiSelectBottomSheet;
